import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Zap, Clock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LANGUAGES } from '../../context/LanguageContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/Button';

export function Hero({ onGetStarted }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleGetStarted = () => {
    const currentLang = location.pathname.split('/')[1];
    const lang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'en';
    navigate(`/${lang}/checkout?plan=auction`);
  };
  const [count, setCount] = useState(0);

  // Animated counter for "spots left"
  useEffect(() => {
    const timer = setInterval(() => {
      setCount(prev => (prev < 7 ? prev + 1 : 7));
    }, 200);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          poster="/images/hero-poster.webp"
        >
          <source src="/video/mixkit-animation-of-futuristic-devices-99786-hd-ready.mp4" type="video/mp4" />
        </video>
        <div className="video-overlay" />
      </div>

      {/* Floating Orbs */}
      <div className="orb orb-1 top-20 left-10" />
      <div className="orb orb-2 bottom-20 right-10" />
      <div className="orb orb-3 top-1/2 left-1/2" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 text-center">
        {/* Urgency Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/40 text-red-400 mb-6 urgency"
        >
          <Clock size={18} className="animate-pulse" />
          <span className="font-bold text-sm tracking-wide">
            {t('hero.badge')} — {count}/10 {t('hero.spotsLeft') || 'spots left'}
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.95] mb-6"
        >
          <span className="block text-white">{t('hero.headline')}</span>
          <span className="block text-shimmer mt-2">{t('hero.headline2')}</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-lg sm:text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-4 px-4"
        >
          {t('hero.subheadline')}
        </motion.p>

        {/* Price */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative inline-block mb-6 px-4"
        >
          <motion.span 
            className="font-heading text-xl sm:text-2xl md:text-4xl font-bold text-primary block"
            animate={{
              opacity: [1, 0.7, 1],
              scale: [1, 1.05, 1]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {t('hero.price')}
          </motion.span>
        </motion.div>

        {/* Value Props */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-3 mb-8 px-4"
        >
          {[
            { icon: Zap, text: t('hero.feature1') },
            { icon: Clock, text: t('hero.feature2') },
            { icon: Sparkles, text: t('hero.feature3') }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10">
              <item.icon size={16} className="text-primary flex-shrink-0" />
              <span className="text-xs sm:text-sm text-slate-300 whitespace-nowrap">{item.text}</span>
            </div>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex items-center justify-center"
        >
          <Button 
            size="lg" 
            onClick={handleGetStarted}
            className="magnetic-btn group text-lg px-10 py-5"
            data-testid="hero-cta-btn"
          >
            <Zap className="inline-block mr-2" size={22} />
            {t('hero.cta')}
            <ArrowRight className="inline-block ml-2 group-hover:translate-x-2 transition-transform" size={22} />
          </Button>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-16 flex flex-wrap items-center justify-center gap-8 text-slate-500"
        >
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm">{t('hero.trust1') || '200+ projects delivered'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm">{t('hero.trust2') || '15+ years experience'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm">{t('hero.trust3') || 'Money-back guarantee'}</span>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-8 h-12 rounded-full border-2 border-primary/50 flex justify-center pt-2"
          >
            <motion.div
              animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-2 rounded-full bg-primary"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
